import { useEffect, useState } from "react";
import axios from "axios";
import {
  Alert,
  Box,
  Button,
  Card,
  CardContent,
  Checkbox,
  CircularProgress,
  Divider,
  FormControl,
  FormControlLabel,
  IconButton,
  Radio,
  RadioGroup,
  TextField,
  Typography,
} from "@mui/material";
import { Add, Delete } from "@mui/icons-material";
import FuseUtils from "../../../utils/FuseUtils";
import { getInputLabelProps, getInputProps, PopperFullWidth } from "../../../common/appStyles";
import localize from "../../../common/localize";
import useGlobalContext from "../../../hooks/useGlobalContext";
import BackgroundForegroundColorPicker from "../../../components/common/BackgroundForegroundColorPicker";
import Combobox from "../../../components/common/Combobox";
import TrueFalseImage from "../../../components/TrueFalseImage";
import EvidenceTempleateItem from "../../evidenceTemplate/components/EvidenceTemplateItem";
import NotificationItem from "../../../components/common/NotificationItem";
import {
  getValueInCurrency,
  MAXIMUM_CHARACTERS_FOR_DESCRIPTION_OR_OBSERVATION,
} from "../../../common/utils";
import {
  getCurrencyFromSetup,
  getUserName,
} from "../../../common/EntitiesManagement";
import NotificationSetup from "../../../components/common/NotificationSetup";
import ExpandableView from "../../../components/common/ExpandableView";
import CreatedByText from "../../../components/common/CreatedByText";
import DeleteConfirmationMessage from "../../../components/common/DeleteConfirmationMessage";
import ErrorMessage from "../../../components/common/ErrorMessage";

export default function EventTypeInfo() {
  const {
    eventTypeModel,
    setEventTypeModel,
    config,
    DYNAMIC_FORM_ENDPOINT,
    WORKFLOW_ENDPOINT,
    OPERATIONAL_AREA_API,
    EVENTTYPE_API,
    PAYMENT_TYPE_ENDPOINT,
    ADDITIONAL_COST_TYPE_ENDPOINT,
    modalConfirm,
    toggleVisibleModalConfirm,
  } = useGlobalContext();
  const [descriptionCount, setDescriptionCount] = useState(0);
  const maximumCharactersForDescription =
    MAXIMUM_CHARACTERS_FOR_DESCRIPTION_OR_OBSERVATION;
  const [dynamicFormData, setDynamicFormData] = useState(null);
  const [workflowData, setWorkflowData] = useState(null);
  const [operationalAreaData, setOperationalAreaData] = useState(null);
  const [paymentTypeOptions, setPaymentTypeOptions] = useState([]);
  const [eventTypePaymentTypes, setEventTypePaymentTypes] = useState([]);
  const [selectedPaymentType, setSelectedPaymentType] = useState(null);
  const [paymentTypeObservation, setPaymentTypeObservation] = useState("");
  const [isAddingPaymentType, setIsAddingPaymentType] = useState(false);
  const [paymentTypeToDelete, setPaymentTypeToDelete] = useState(null);
  const [additionalCostTypeOptions, setAdditionalCostTypeOptions] = useState(
    [],
  );
  const [eventTypeAdditionalCosts, setEventTypeAdditionalCosts] = useState([]);
  const [selectedAdditionalCostType, setSelectedAdditionalCostType] =
    useState(null);
  const [additionalCostValue, setAdditionalCostValue] = useState(0);
  const [additionalCostObservation, setAdditionalCostObservation] =
    useState("");
  const [additionalCostNeedApproval, setAdditionalCostNeedApproval] =
    useState(true);
  const [isAddingAdditionalCost, setIsAddingAdditionalCost] = useState(false);
  const [additionalCostToDelete, setAdditionalCostToDelete] = useState(null);
  const [showErrorModal, setShowErrorModal] = useState(false);
  const [errorMessage, setErrorMessage] = useState("");

  const extractAdditionalInfo = (jsonData) => {
    if (jsonData && jsonData.length > 0) {
      const data = JSON.parse(jsonData);

      return data;
    }

    return [];
  };

  const [additionalData, setAdditionalData] = useState(
    extractAdditionalInfo(eventTypeModel.AdditionalFieldsJson)
  );
  const [newAdditionalDataName, setNewAdditionalDataName] = useState("");

  const getDynamicFormData = async () => {
    try {
      const requestConfig = {
        ...config,
        headers: { ...config.headers, pagination: -1 },
      };
      const data = await axios.get(`${DYNAMIC_FORM_ENDPOINT}/`, requestConfig);
      setDynamicFormData(data?.data);
    } catch {
      setDynamicFormData(null);
    }
  };

  const getWorkflowData = async () => {
    try {
      const requestConfig = {
        ...config,
        headers: { ...config.headers, pagination: -1 },
      };
      const data = await axios.get(`${WORKFLOW_ENDPOINT}/`, requestConfig);
      setWorkflowData(data?.data);
    } catch {
      setWorkflowData(null);
    }
  };

  const getOperationalAreaData = async () => {
    try {
      const requestConfig = {
        ...config,
        headers: { ...config.headers, pagination: -1 },
      };
      const data = await axios.get(`${OPERATIONAL_AREA_API}/`, requestConfig);
      setOperationalAreaData(data?.data);
    } catch {
      setOperationalAreaData(null);
    }
  };

  const getPaymentTypeOptions = async () => {
    try {
      const requestConfig = {
        ...config,
        headers: {
          ...config.headers,
          pagination: -1,
        },
      };
      const data = await axios.get(`${PAYMENT_TYPE_ENDPOINT}`, requestConfig);
      setPaymentTypeOptions(data?.data?.Data ?? []);
    } catch {
      setPaymentTypeOptions([]);
    }
  };

  const getEventTypePaymentTypes = async () => {
    if (!eventTypeModel?.Id || eventTypeModel.Id <= 0) {
      setEventTypePaymentTypes([]);
      return;
    }
    try {
      const requestConfig = {
        ...config,
        headers: {
          ...config.headers,
          pagination: -1,
        },
      };
      const data = await axios.get(
        `${EVENTTYPE_API}/${eventTypeModel.Id}/paymentType`,
        requestConfig,
      );
      setEventTypePaymentTypes(data?.data?.Data ?? data?.data ?? []);
    } catch {
      setEventTypePaymentTypes([]);
    }
  };

  const handleAddPaymentType = async () => {
    if (!selectedPaymentType?.Id || !eventTypeModel?.Id) return;
    setIsAddingPaymentType(true);
    try {
      await axios.post(
        `${EVENTTYPE_API}/${eventTypeModel.Id}/paymentType`,
        {
          EventTypeId: eventTypeModel.Id,
          PaymentTypeId: selectedPaymentType.Id,
          Observation: paymentTypeObservation ?? "",
          CreatedBy: getUserName(),
        },
        config,
      );
      setSelectedPaymentType(null);
      setPaymentTypeObservation("");
      await getEventTypePaymentTypes();
    } catch {
      // swallow; surface errors via the existing global error handling chain
    } finally {
      setIsAddingPaymentType(false);
    }
  };

  const handleDeletePaymentTypeRequest = (item) => {
    setPaymentTypeToDelete(item);
    toggleVisibleModalConfirm();
  };

  const handleConfirmDeletePaymentType = async () => {
    if (!paymentTypeToDelete || !eventTypeModel?.Id) {
      toggleVisibleModalConfirm();
      setPaymentTypeToDelete(null);
      return;
    }
    try {
      await axios.delete(
        `${EVENTTYPE_API}/${eventTypeModel.Id}/paymentType/${
          paymentTypeToDelete?.paymentType?.Id
        }`,
        config,
      );
    } catch {
      // ignore for now
    }
    toggleVisibleModalConfirm();
    setPaymentTypeToDelete(null);
    await getEventTypePaymentTypes();
  };

  const formatPaymentTypeValue = (paymentType) => {
    if (!paymentType) return localize.no_data;
    if (paymentType.Type === 1) {
      return `${paymentType.DefaultValue ?? 0}%`;
    }
    return getValueInCurrency(
      getCurrencyFromSetup(),
      paymentType.DefaultValue ?? 0,
    );
  };

  const getAdditionalCostTypeOptions = async () => {
    try {
      const data = await axios.get(`${ADDITIONAL_COST_TYPE_ENDPOINT}`, config);
      if (data?.data?.Ok && data?.data?.Data?.length > 0) {
        setAdditionalCostTypeOptions(data.data.Data);
      } else {
        setAdditionalCostTypeOptions([]);
      }
    } catch {
      setAdditionalCostTypeOptions([]);
    }
  };

  const getEventTypeAdditionalCosts = async () => {
    if (!eventTypeModel?.Id || eventTypeModel.Id <= 0) {
      setEventTypeAdditionalCosts([]);
      return;
    }
    try {
      const requestConfig = {
        ...config,
        headers: {
          ...config.headers,
          pagination: -1,
        },
      };
      const data = await axios.get(
        `${EVENTTYPE_API}/${eventTypeModel.Id}/additionalCost`,
        requestConfig,
      );
      setEventTypeAdditionalCosts(data?.data?.Data ?? data?.data ?? []);
    } catch {
      setEventTypeAdditionalCosts([]);
    }
  };

  const handleAdditionalCostTypeChange = (value) => {
    if (!value) {
      setSelectedAdditionalCostType(null);
      return;
    }
    setSelectedAdditionalCostType(value);
    if (value.DefaultValue !== undefined && value.DefaultValue !== null) {
      setAdditionalCostValue(value.DefaultValue);
    }
  };

  const handleAddAdditionalCost = async () => {
    if (!selectedAdditionalCostType?.Id || !eventTypeModel?.Id) return;
    setIsAddingAdditionalCost(true);
    try {
      const response = await axios.post(
        `${EVENTTYPE_API}/${eventTypeModel.Id}/additionalCost`,
        {
          value: Number(additionalCostValue) || 0,
          additionalCostTypeId: selectedAdditionalCostType.Id,
          observations: additionalCostObservation ?? "",
          needApproval: additionalCostNeedApproval,
          createdBy: getUserName(),
        },
        config,
      );

      if (response?.data && response.data.Ok === false) {
        setErrorMessage(
          response.data.Data ?? localize.error_unexpected,
        );
        setShowErrorModal(true);
        return;
      }

      setSelectedAdditionalCostType(null);
      setAdditionalCostValue(0);
      setAdditionalCostObservation("");
      setAdditionalCostNeedApproval(true);
      await getEventTypeAdditionalCosts();
    } catch (error) {
      setErrorMessage(
        error?.response?.data?.Data ??
          error?.response?.data ??
          error?.message ??
          localize.error_unexpected,
      );
      setShowErrorModal(true);
    } finally {
      setIsAddingAdditionalCost(false);
    }
  };

  const handleDeleteAdditionalCostRequest = (item) => {
    setAdditionalCostToDelete(item);
    toggleVisibleModalConfirm();
  };

  const handleConfirmDeleteAdditionalCost = async () => {
    if (!additionalCostToDelete || !eventTypeModel?.Id) {
      toggleVisibleModalConfirm();
      setAdditionalCostToDelete(null);
      return;
    }
    try {
      await axios.delete(
        `${EVENTTYPE_API}/${eventTypeModel.Id}/additionalCost/${
          (additionalCostToDelete.additionalCost ??
            additionalCostToDelete.AdditionalCost)?.Id ??
          additionalCostToDelete.Id ??
          additionalCostToDelete.id
        }`,
        config,
      );
    } catch {
      // ignore
    }
    toggleVisibleModalConfirm();
    setAdditionalCostToDelete(null);
    await getEventTypeAdditionalCosts();
  };

  useEffect(() => {
    getDynamicFormData();
    getWorkflowData();
    getOperationalAreaData();
    getPaymentTypeOptions();
    getAdditionalCostTypeOptions();
  }, []);

  useEffect(() => {
    getEventTypePaymentTypes();
    getEventTypeAdditionalCosts();
  }, [eventTypeModel?.Id]);

  const handleReviewFormChange = (value) => {
    if (!value) {
      setEventTypeModel({
        ...eventTypeModel,
        ReviewFormId: 0,
        ReviewForm: null,
      });
      return;
    }

    setEventTypeModel({
      ...eventTypeModel,
      ReviewFormId: value.Id,
      ReviewForm: value,
    });
  };

  const handleOperationalAreaChange = (value) => {
    if (!value) {
      setEventTypeModel({
        ...eventTypeModel,
        OperationalAreaId: 0,
        operationalArea: null,
      });
      return;
    }

    setEventTypeModel({
      ...eventTypeModel,
      OperationalAreaId: value.Id,
      operationalArea: value,
      BackgroundColor: value.BackgroundColor,
      ForegroundColor: value.ForegroundColor,
    });
  };

  const handleWorkflowChange = (value) => {
    if (!value) {
      setEventTypeModel({
        ...eventTypeModel,
        WorkFlowId: 0,
        Workflow: null,
      });
      return;
    }

    setEventTypeModel({
      ...eventTypeModel,
      WorkFlowId: value.Id,
      Workflow: value,
    });
  };

  const handleChange = (e) => {
    if (e.target.name === "Description") {
      setDescriptionCount(e.target.value.length);
    }

    if (
      [
        "AttentionDays",
        "SemaphoreRed",
        "SemaphoreYellow",
        "SemaphoreRed",
      ].includes(e.target.name) &&
      e.target.value < 0
    ) {
      setEventTypeModel({ ...eventTypeModel, [e.target.name]: 0 });
      return;
    }

    setEventTypeModel({ ...eventTypeModel, [e.target.name]: e.target.value });
  };

  const handleNewAdditionalFieldNameChange = (e) => {
    setNewAdditionalDataName(e.target.value);
  };

  const handleAddBtn = () => {
    additionalData.push({
      guid: FuseUtils.generateGUID(),
      name: newAdditionalDataName,
      description: "",
    });

    setNewAdditionalDataName("");
  };

  const handleDeleteButton = (guid) => {
    const index = additionalData.findIndex((x) => x.guid === guid);
    if (index >= 0) {
      additionalData.splice(index, 1);
      setAdditionalData([...additionalData]);
    }
  };

  useEffect(() => {
    setEventTypeModel({
      ...eventTypeModel,
      AdditionalFields: additionalData,
    });
  }, [additionalData]);

  const handleCheckedChanged = (e) => {
    setEventTypeModel({
      ...eventTypeModel,
      [e.target.name]: e.target.checked,
    });
  };

  return (
    <div style={{ width: "100%" }}>
      <ExpandableView
        components={[
          {
            Name: localize.general_information,
            Component: (
              <form className="w-full flex flex-col gap-2 p-2">
                <div className="flex flex-wrap my-2 w-full">
                  <div className="w-full">
                    <TextField
                      required
                      InputLabelProps={getInputLabelProps()}
                      inputProps={getInputProps()}
                      size="small"
                      fullWidth
                      id="outlined-basic"
                      name="Name"
                      label={localize.name}
                      variant="outlined"
                      onChange={handleChange}
                      value={eventTypeModel.Name}
                    />
                  </div>
                </div>

                <div className="w-full flex gap-2 my-2">
                  <FormControl required fullWidth>
                    <Combobox
                      isRequired
                      id="operationalAreaId"
                      fieldLabel={localize.operational_area}
                      fieldOptions={operationalAreaData?.Data ?? []}
                      fieldValue={eventTypeModel.operationalArea}
                      handleValueChange={handleOperationalAreaChange}
                    />
                  </FormControl>
                </div>

                <div className="w-full flex gap-2 my-2">
                  <FormControl required fullWidth>
                    <Combobox
                      isRequired
                      id="WorkFlowId"
                      fieldLabel={localize.workflow}
                      fieldOptions={workflowData?.Data ?? []}
                      fieldValue={eventTypeModel?.Workflow ?? null}
                      handleValueChange={handleWorkflowChange}
                    />
                  </FormControl>
                </div>

                <div className="my-2" style={{ marginBottom: "10px" }}>
                  <FormControl required fullWidth>
                    <Combobox
                      id="ReviewFormId"
                      fieldLabel={localize.visit_report}
                      fieldOptions={dynamicFormData?.Data ?? []}
                      fieldValue={eventTypeModel?.ReviewForm ?? null}
                      handleValueChange={handleReviewFormChange}
                    />
                  </FormControl>
                </div>

                <div className="flex flex-wrap my-2 w-full">
                  <div className="w-full">
                    <TextField
                      required
                      InputLabelProps={getInputLabelProps()}
                      inputProps={{
                        ...getInputProps(),
                        maxLength: 100,
                      }}
                      size="small"
                      fullWidth
                      id="outlined-basic"
                      name="reportTitle"
                      label={localize.report_title}
                      variant="outlined"
                      onChange={handleChange}
                      value={eventTypeModel.reportTitle}
                    />
                  </div>
                </div>

                <div className="w-full flex gap-2 my-2">
                  <div style={{ width: "50%" }}>
                    <TextField
                      type="number"
                      InputLabelProps={getInputLabelProps()}
                      inputProps={getInputProps()}
                      value={eventTypeModel.executionValue}
                      onChange={handleChange}
                      size="small"
                      id="executionValue"
                      name="executionValue"
                      label={localize.service_value}
                      variant="outlined"
                      fullWidth
                    />
                  </div>

                  <div style={{ width: "50%" }}>
                    <FormControlLabel
                      size="small"
                      name="RequestSignature"
                      control={
                        <Checkbox
                          size="small"
                          name="RequestSignature"
                          checked={eventTypeModel.RequestSignature}
                          onChange={handleCheckedChanged}
                        />
                      }
                      label={
                        <span style={{ fontSize: "0.8em" }}>
                          {localize.request_signature}
                        </span>
                      }
                    />
                  </div>
                </div>

                <div className="flex flex-wrap my-2 w-full">
                  <div className="w-full">
                    <TextField
                      InputLabelProps={getInputLabelProps()}
                      inputProps={{
                        ...getInputProps(),
                        maxLength: maximumCharactersForDescription,
                      }}
                      size="small"
                      fullWidth
                      multiline
                      rows={3}
                      id="outlined-basic"
                      name="Description"
                      label={localize.description}
                      variant="outlined"
                      onChange={handleChange}
                      value={eventTypeModel.Description}
                      helperText={`${descriptionCount}/${maximumCharactersForDescription}`}
                    />
                  </div>
                </div>

                <Box className="flex flex-col gap-2 items-center">
                  <Alert severity="info" className="w-full">
                    <label className="text-xs">
                      {localize.color_explanation.replace(
                        "%1s",
                        localize.color_explanation_complement_event_type
                      )}
                    </label>
                  </Alert>
                  <BackgroundForegroundColorPicker
                    backgroundValue={eventTypeModel.BackgroundColor}
                    foregroundValue={eventTypeModel.ForegroundColor}
                    onChange={handleChange}
                  />
                </Box>
              </form>
            ),
          },
          {
            Name: localize.attention,
            Component: (
              <Box className="mt-4 flex flex-col gap-2">
                <Card variant="outlined" sx={{ borderRadius: "8px" }}>
                  <CardContent sx={{ "&:last-child": { pb: 1.5 }, p: 1.5 }}>
                    <Box className="flex gap-2 w-full items-center">
                      <div style={{ width: "35%" }}>
                        <TextField
                          type="number"
                          InputLabelProps={getInputLabelProps()}
                          inputProps={getInputProps()}
                          value={eventTypeModel.AttentionDays}
                          onChange={handleChange}
                          size="small"
                          id="AttentionDays"
                          name="AttentionDays"
                          label={localize.days_quantity}
                          variant="outlined"
                          fullWidth
                        />
                      </div>

                      <div style={{ width: "65%" }}>
                          <label className="text-xs">
                            {localize.info_attention_days}
                          </label>
                      </div>
                      
                    </Box>
                  </CardContent>
                </Card>

                <Card variant="outlined" sx={{ borderRadius: "8px" }}>
                  <CardContent sx={{ "&:last-child": { pb: 1.5 }, p: 1.5 }}>
                    <Box className="flex gap-2 w-full items-center">
                      <div style={{ width: "35%" }}>
                        <TextField
                          type="number"
                          InputLabelProps={getInputLabelProps()}
                          inputProps={getInputProps()}
                          value={eventTypeModel.SemaphoreYellow}
                          onChange={handleChange}
                          size="small"
                          id="SemaphoreYellow"
                          name="SemaphoreYellow"
                          label={localize.days_quantity}
                          variant="outlined"
                          fullWidth
                        />
                      </div>

                      <div style={{ width: "65%" }}>
                        <label className="text-xs">
                          {localize.event_yellow_semaphore}
                        </label>
                      </div>
                      
                    </Box>
                  </CardContent>
                </Card>

                <Card variant="outlined" sx={{ borderRadius: "8px" }}>
                  <CardContent sx={{ "&:last-child": { pb: 1.5 }, p: 1.5 }}>
                    <Box className="flex gap-2 w-full items-center">
                      <div style={{ width: "35%" }}>
                        <TextField
                          type="number"
                          InputLabelProps={getInputLabelProps()}
                          inputProps={getInputProps()}
                          value={eventTypeModel.SemaphoreRed}
                          onChange={handleChange}
                          size="small"
                          id="SemaphoreRed"
                          name="SemaphoreRed"
                          label={localize.days_quantity}
                          variant="outlined"
                          fullWidth
                        />
                      </div>

                        <div style={{ width: "65%" }}>
                          <label className="text-xs">
                            {localize.event_red_semaphore}
                          </label>
                        </div>
                      
                    </Box>
                  </CardContent>
                </Card>

                
              </Box>
            ),
          },
          {
            Name: localize.additional_data,
            Component: (
              <div className="flex flex-col my-4 w-full p-2">
                <div
                  className="flex items-center w-full"
                  style={{ gap: "10px", marginBottom: "10px" }}
                >
                  <TextField
                    InputLabelProps={getInputLabelProps()}
                    inputProps={{
                      ...getInputProps(),
                    }}
                    size="small"
                    fullWidth
                    id="outlined-basic"
                    name="new_additional_field_name"
                    label={localize.enter_new_field_name}
                    variant="outlined"
                    onChange={handleNewAdditionalFieldNameChange}
                    value={newAdditionalDataName}
                    onKeyUp={(event) => {
                      if (event.key === "Enter") {
                        handleAddBtn();
                      }
                    }}
                  />
                  <Button
                    size="small"
                    variant="contained"
                    color="success"
                    className="app_general_button_data app_success_button btn-sm"
                    onClick={handleAddBtn}
                    disabled={
                      !newAdditionalDataName ||
                      newAdditionalDataName.length === 0
                    }
                    style={{ width: "130px" }}
                  >
                    <label className="mr-2 report_little_text">
                      {localize.add}
                    </label>
                    <Add className="text-lg" />
                  </Button>
                </div>

                <Divider />

                <Box className="w-full flex flex-col gap-2 mt-2">
                  {additionalData && additionalData.length > 0 ? (
                    additionalData.map((field) => (
                      <EvidenceTempleateItem
                        key={field.guid}
                        evidence={{
                          Name: field.name,
                        }}
                        onDeleteEvidence={() => {
                          handleDeleteButton(field.guid);
                        }}
                      />
                    ))
                  ) : (
                    <NotificationItem
                      notification={{
                        type: 0,
                        message: localize.no_data,
                      }}
                    />
                  )}
                </Box>
              </div>
            ),
          },
          (eventTypeModel?.Id > 0 && {
            Name: localize.payment_types,
            Component: (
              <Box className="flex flex-col gap-3 p-2 w-full">
                <FormControl fullWidth>
                  <Combobox
                    id="paymentTypeSelector"
                    fieldLabel={localize.payment_type}
                    fieldOptions={paymentTypeOptions ?? []}
                    fieldValue={selectedPaymentType}
                    handleValueChange={(value) =>
                      setSelectedPaymentType(value ?? null)
                    }
                    customOptionLabel={(option) =>
                      `${option.Name} (${
                        option.Type === 1
                          ? localize.percentage_value
                          : localize.fixed_value
                      })`
                    }
                  />
                </FormControl>

                <TextField
                  InputLabelProps={getInputLabelProps()}
                  inputProps={{
                    ...getInputProps(),
                    maxLength: maximumCharactersForDescription,
                  }}
                  size="small"
                  fullWidth
                  multiline
                  rows={2}
                  id="paymentTypeObservation"
                  name="paymentTypeObservation"
                  label={localize.observations}
                  variant="outlined"
                  onChange={(e) => setPaymentTypeObservation(e.target.value)}
                  value={paymentTypeObservation}
                />

                <Box className="flex justify-end">
                  <Button
                    size="small"
                    variant="contained"
                    color="success"
                    className="app_general_button_data app_success_button btn-sm"
                    onClick={handleAddPaymentType}
                    disabled={
                      !selectedPaymentType?.Id ||
                      !eventTypeModel?.Id ||
                      eventTypeModel.Id <= 0 ||
                      isAddingPaymentType
                    }
                    style={{ minWidth: "130px" }}
                  >
                    <label className="mr-2 report_little_text">
                      {localize.add}
                    </label>
                    {isAddingPaymentType ? (
                      <CircularProgress size={16} sx={{ color: "white" }} />
                    ) : (
                      <Add className="text-lg" />
                    )}
                  </Button>
                </Box>

                <Divider />

                <Box className="flex flex-col gap-2 mt-2">
                  {eventTypePaymentTypes && eventTypePaymentTypes.length > 0 ? (
                    eventTypePaymentTypes.map((item) => {
                      const paymentType = item?.paymentType ?? item?.PaymentType;
                      return (
                        <Card
                          key={item.id ?? item.Id}
                          variant="outlined"
                          sx={{ borderRadius: "8px" }}
                        >
                          <CardContent
                            sx={{
                              "&:last-child": { pb: 1.5 },
                              p: 1.5,
                            }}
                          >
                            <Box className="flex justify-between items-center gap-2">
                              <Box className="flex flex-col gap-1 flex-1">
                                <Typography
                                  sx={{
                                    fontSize: "0.85rem",
                                    fontWeight: 700,
                                  }}
                                >
                                  {paymentType?.Name ?? localize.no_data}
                                </Typography>
                                <Box className="flex items-center gap-2 flex-wrap">
                                  <Typography
                                    sx={{
                                      fontSize: "0.7rem",
                                      px: 0.75,
                                      py: 0.25,
                                      borderRadius: "6px",
                                      backgroundColor: "#EEF2FF",
                                      color: "#3730A3",
                                      fontWeight: 600,
                                    }}
                                  >
                                    {paymentType?.Type === 1
                                      ? localize.percentage_value
                                      : localize.fixed_value}
                                  </Typography>
                                  <Typography
                                    sx={{
                                      fontSize: "0.8rem",
                                      fontWeight: 600,
                                      color: "#0F766E",
                                    }}
                                  >
                                    {formatPaymentTypeValue(paymentType)}
                                  </Typography>
                                </Box>
                                {item?.Observation && (
                                  <Typography
                                    sx={{
                                      fontSize: "0.75rem",
                                      color: "#4B5563",
                                    }}
                                  >
                                    <b>{`${localize.observations}: `}</b>
                                    {item.Observation}
                                  </Typography>
                                )}
                              </Box>
                              <IconButton
                                size="small"
                                onClick={() =>
                                  handleDeletePaymentTypeRequest(item)
                                }
                              >
                                <Delete
                                  sx={{
                                    color: "crimson",
                                    width: 18,
                                    height: 18,
                                  }}
                                />
                              </IconButton>
                            </Box>
                          </CardContent>
                        </Card>
                      );
                    })
                  ) : (
                    <NotificationItem
                      notification={{
                        type: 0,
                        message: localize.no_data,
                      }}
                    />
                  )}
                </Box>
              </Box>
            ),
          }),
          (eventTypeModel?.Id > 0 && {
            Name: localize.additional_costs,
            Component: (
              <Box className="flex flex-col gap-3 p-2 w-full">
                <FormControl fullWidth>
                  <Combobox
                    id="additionalCostTypeSelector"
                    fieldLabel={localize.additional_cost_type}
                    fieldOptions={additionalCostTypeOptions ?? []}
                    fieldValue={selectedAdditionalCostType}
                    handleValueChange={handleAdditionalCostTypeChange}
                  />
                </FormControl>

                <TextField
                  type="number"
                  InputLabelProps={getInputLabelProps()}
                  inputProps={getInputProps()}
                  size="small"
                  fullWidth
                  id="additionalCostValue"
                  name="additionalCostValue"
                  label={localize.value}
                  variant="outlined"
                  value={additionalCostValue}
                  onChange={(e) => setAdditionalCostValue(e.target.value)}
                />

                <TextField
                  InputLabelProps={getInputLabelProps()}
                  inputProps={{
                    ...getInputProps(),
                    maxLength: maximumCharactersForDescription,
                  }}
                  size="small"
                  fullWidth
                  multiline
                  rows={3}
                  id="additionalCostObservation"
                  name="additionalCostObservation"
                  label={localize.observations}
                  variant="outlined"
                  onChange={(e) =>
                    setAdditionalCostObservation(e.target.value)
                  }
                  value={additionalCostObservation}
                />

                <Box className="flex justify-between">
                  <Box className="flex flex-col">
                  <Typography
                    sx={{
                      fontSize: "0.8rem",
                      fontWeight: 600,
                      color: "#374151",
                    }}
                  >
                    {localize.need_approval_question ?? "¿Necesita aprobación?"}
                  </Typography>
                  <RadioGroup
                    row
                    value={additionalCostNeedApproval ? "yes" : "no"}
                    onChange={(e) =>
                      setAdditionalCostNeedApproval(e.target.value === "yes")
                    }
                  >
                    <FormControlLabel
                      value="yes"
                      control={<Radio size="small" />}
                      label={
                        <span style={{ fontSize: "0.8rem" }}>
                          {localize.yes}
                        </span>
                      }
                    />
                    <FormControlLabel
                      value="no"
                      control={<Radio size="small" />}
                      label={
                        <span style={{ fontSize: "0.8rem" }}>
                          {localize.no}
                        </span>
                      }
                    />
                  </RadioGroup>
                </Box>

                  <Button
                    size="small"
                    variant="contained"
                    color="success"
                    className="app_general_button_data app_success_button btn-sm"
                    onClick={handleAddAdditionalCost}
                    disabled={
                      !selectedAdditionalCostType?.Id ||
                      !eventTypeModel?.Id ||
                      eventTypeModel.Id <= 0 ||
                      isAddingAdditionalCost
                    }
                    style={{ minWidth: "130px" }}
                  >
                    <label className="mr-2 report_little_text">
                      {localize.add}
                    </label>
                    {isAddingAdditionalCost ? (
                      <CircularProgress size={16} sx={{ color: "white" }} />
                    ) : (
                      <Add className="text-lg" />
                    )}
                  </Button>
                </Box>

                <Divider />

                <Box className="flex flex-col gap-2 mt-2">
                  {eventTypeAdditionalCosts &&
                  eventTypeAdditionalCosts.length > 0 ? (
                    eventTypeAdditionalCosts.map((item) => {
                      const additionalCost =
                        item?.additionalCost ?? item?.AdditionalCost;
                      const additionalCostType =
                        additionalCost?.additionalCostType ??
                        additionalCost?.AdditionalCostType;
                      return (
                        <Card
                          key={item.Id ?? item.id}
                          variant="outlined"
                          sx={{ borderRadius: "8px" }}
                        >
                          <CardContent
                            sx={{
                              "&:last-child": { pb: 1.5 },
                              p: 1.5,
                            }}
                          >
                            <Box className="flex justify-between items-center gap-2">
                              <Box className="flex flex-col gap-1 flex-1">
                                <Typography
                                  sx={{
                                    fontSize: "0.85rem",
                                    fontWeight: 700,
                                  }}
                                >
                                  {additionalCostType?.Name ?? localize.no_data}
                                </Typography>
                                <Typography
                                  sx={{
                                    fontSize: "0.8rem",
                                    fontWeight: 600,
                                    color: "#0F766E",
                                  }}
                                >
                                  {getValueInCurrency(
                                    getCurrencyFromSetup(),
                                    additionalCost?.value ?? 0,
                                  )}
                                </Typography>
                                {additionalCost?.observations && (
                                  <Typography
                                    sx={{
                                      fontSize: "0.75rem",
                                      color: "#4B5563",
                                    }}
                                  >
                                    <b>{`${localize.observations}: `}</b>
                                    {additionalCost.observations}
                                  </Typography>
                                )}
                                <Box className="flex items-center gap-1">
                                  <Typography
                                    sx={{
                                      fontSize: "0.75rem",
                                      fontWeight: 600,
                                      color: "#374151",
                                    }}
                                  >
                                    {`${
                                      localize.need_approval_question ??
                                      "¿Necesita aprobación?"
                                    }`}
                                  </Typography>
                                  <TrueFalseImage
                                    notCentered
                                    value={
                                      additionalCost?.needApproval ??
                                      additionalCost?.NeedApproval ??
                                      false
                                    }
                                    toolTip={
                                      (additionalCost?.needApproval ??
                                      additionalCost?.NeedApproval)
                                        ? localize.yes
                                        : localize.no
                                    }
                                  />
                                </Box>
                              </Box>
                              <IconButton
                                size="small"
                                onClick={() =>
                                  handleDeleteAdditionalCostRequest(item)
                                }
                              >
                                <Delete
                                  sx={{
                                    color: "crimson",
                                    width: 18,
                                    height: 18,
                                  }}
                                />
                              </IconButton>
                            </Box>
                          </CardContent>
                        </Card>
                      );
                    })
                  ) : (
                    <NotificationItem
                      notification={{
                        type: 0,
                        message: localize.no_data,
                      }}
                    />
                  )}
                </Box>
              </Box>
            ),
          }),
          {
            Name: localize.notification,
            Component: (
              <NotificationSetup
                entity={eventTypeModel}
                setEntity={setEventTypeModel}
                subtitle={localize.notify_event_type}
                showOnlyDefinedToUser
              />
            ),
          },
        ].filter(Boolean)}
      />

      <CreatedByText entity={eventTypeModel} />

      {modalConfirm && paymentTypeToDelete && (
        <DeleteConfirmationMessage
          entityType={localize.payment_type}
          entityName={
            (paymentTypeToDelete?.paymentType ?? paymentTypeToDelete?.PaymentType)
              ?.Name ?? ""
          }
          onClickForCancel={() => {
            toggleVisibleModalConfirm();
            setPaymentTypeToDelete(null);
          }}
          onClickForConfirm={handleConfirmDeletePaymentType}
        />
      )}

      {modalConfirm && additionalCostToDelete && (
        <DeleteConfirmationMessage
          entityType={localize.additional_cost_type}
          entityName={
            (additionalCostToDelete?.additionalCost ??
              additionalCostToDelete?.AdditionalCost)?.additionalCostType
              ?.Name ?? ""
          }
          onClickForCancel={() => {
            toggleVisibleModalConfirm();
            setAdditionalCostToDelete(null);
          }}
          onClickForConfirm={handleConfirmDeleteAdditionalCost}
        />
      )}

      {showErrorModal && (
        <ErrorMessage
          message={errorMessage}
          title={localize.error_unexpected}
          showModal={showErrorModal}
          setShowModal={setShowErrorModal}
        />
      )}
    </div>
  );
}
